import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { formatDelta, deltaSign } from "../../utils/gamification/format";

// Flat "ink" primitives for gamification v2 screens: typographic emphasis,
// hairlines, no boxes/gradients. Colours are deliberately few.
export const INK = {
  text: "#FFFFFF",
  muted: "#8A8A8A",
  dim: "#555555",
  rule: "rgba(255,255,255,0.10)",
  accent: "#FFEE20",
};

// Large numeral with a small label under it.
export function Numeral({ value, label, sub, size = 56, sx }) {
  return (
    <Box sx={sx}>
      <Typography component="div" sx={{ fontSize: size, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.03em", color: INK.text, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      {label ? <Typography sx={{ mt: 0.75, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: INK.muted }}>{label}</Typography> : null}
      {sub ? <Typography sx={{ mt: 0.25, fontSize: 13, color: INK.muted }}>{sub}</Typography> : null}
    </Box>
  );
}

export function Hairline({ sx }) {
  return <Box sx={{ height: "1px", bgcolor: INK.rule, width: "100%", ...sx }} />;
}

// Thin progress hairline; value/max in any unit.
export function HairlineBar({ value, max = 100, sx }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Number(value) / max) * 100)) : 0;
  return (
    <Box role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} sx={{ position: "relative", height: "2px", bgcolor: INK.rule, width: "100%", ...sx }}>
      <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, bgcolor: INK.text }} />
    </Box>
  );
}

// Small-caps section label with an optional right-hand node and a hairline under it.
export function SectionLabel({ children, right, sx }) {
  return (
    <Box sx={{ mt: 3, mb: 1.5, ...sx }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: INK.muted }}>{children}</Typography>
        {right ? <Box sx={{ fontSize: 12, color: INK.muted }}>{right}</Box> : null}
      </Stack>
      <Hairline />
    </Box>
  );
}

// Signed change: positive in accent, negative muted, flat dim.
export function Delta({ value, previous, sx }) {
  const text = formatDelta(value, previous);
  const delta = previous === undefined ? value : Number(value) - Number(previous);
  const sign = deltaSign(delta);
  const color = sign > 0 ? INK.accent : sign < 0 ? INK.muted : INK.dim;
  return <Typography component="span" sx={{ fontSize: 12, color, fontVariantNumeric: "tabular-nums", ...sx }}>{text}</Typography>;
}

// Text toggle: "7 · 30 · 90". Active option is white with an underline.
export function Choice({ options, value, onChange, ariaLabel, sx }) {
  return (
    <Stack direction="row" role="group" aria-label={ariaLabel} sx={{ flexWrap: "wrap", alignItems: "baseline", ...sx }}>
      {options.map((opt, i) => {
        const active = opt.value === value;
        return (
          <React.Fragment key={String(opt.value)}>
            {i > 0 ? <Typography component="span" sx={{ color: INK.dim, mx: 0.75, fontSize: 12 }}>·</Typography> : null}
            <Box
              component="button"
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              sx={{
                background: "none", border: 0, p: 0, cursor: "pointer", fontFamily: "inherit",
                fontSize: 13, fontWeight: active ? 700 : 400, color: active ? INK.text : INK.muted,
                borderBottom: active ? `1px solid ${INK.text}` : "1px solid transparent", lineHeight: 1.4,
                "&:hover": { color: INK.text },
              }}
            >
              {opt.label}
            </Box>
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

// List row: primary/secondary text on the left, anything on the right, hairline under.
export function Row({ primary, secondary, right, onClick, leading, sx, ...rest }) {
  return (
    <Box
      onClick={onClick}
      {...rest}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, py: 1.25, borderBottom: `1px solid ${INK.rule}`,
        cursor: onClick ? "pointer" : "default", minWidth: 0, ...sx,
        ...(onClick ? { "&:hover .ink-primary": { color: INK.accent } } : {}),
      }}
    >
      {leading !== undefined ? <Box sx={{ flexShrink: 0 }}>{leading}</Box> : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography className="ink-primary" sx={{ fontSize: 14, color: INK.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{primary}</Typography>
        {secondary ? <Typography sx={{ fontSize: 12, color: INK.muted, mt: 0.25 }}>{secondary}</Typography> : null}
      </Box>
      {right !== undefined ? <Box sx={{ flexShrink: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{right}</Box> : null}
    </Box>
  );
}

export function Muted({ children, sx, ...rest }) {
  return <Typography sx={{ fontSize: 13, color: INK.muted, lineHeight: 1.5, ...sx }} {...rest}>{children}</Typography>;
}

// Text-link button: muted, accent with an underline on hover or focus.
export function TextAction({ children, onClick, sx, ...rest }) {
  return (
    <Box
      component="button" type="button" onClick={onClick} {...rest}
      sx={{
        background: "none", border: 0, p: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, lineHeight: 1.4, color: INK.muted,
        borderBottom: "1px solid transparent", "&:hover, &:focus-visible": { color: INK.accent, borderBottomColor: INK.accent, outline: "none" }, ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// Rank column for leaderboards: two-digit tabular numeral.
export function Rank({ value }) {
  return <Typography sx={{ width: 28, fontSize: 13, color: INK.dim, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{value}</Typography>;
}

// Shared MUI Tabs styling (mirrors Library) so v2 pages read as one system.
export const inkTabsSx = {
  mb: 3,
  borderBottom: `1px solid ${INK.rule}`,
  minHeight: 40,
  "& .MuiTab-root": { color: INK.muted, fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem" }, textTransform: "none", minWidth: "auto", minHeight: 40, px: { xs: 1.25, sm: 2 }, mr: { xs: 0.5, sm: 2 }, letterSpacing: 0.3, "&:hover": { color: INK.text } },
  "& .Mui-selected": { color: `${INK.text} !important` },
  "& .MuiTabs-scrollButtons": { color: INK.muted, "&.Mui-disabled": { opacity: 0.3 } },
};
